import { DatePicker } from "antd";
import locale from "antd/locale/fr_FR";
import classnames from "classnames";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineKeyboard } from "react-icons/md";
import useStableDatePickerValue from "shared/hooks/useStableDatePickerValue";
import { NAME_SPACES } from "shared/locales/constants";
import Label from "../../../Label";

const Simple = ({
  label,
  errors,
  icon,
  touched,
  props,
  direction,
  required,
  optional,
  right,
  ...rest
}) => {
  const { t } = useTranslation(NAME_SPACES.COMPONENTS);
  const DATE_PICKER = t("DATE_PICKER", { returnObjects: true });

  const [clickCount, setClickCount] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const inputRef = useRef(null);

  const formatDate = (event) => {
    const input = event.target;
    const raw = input.value.replace(/\D/g, "");

    let day = raw.slice(0, 2);
    let month = raw.slice(2, 4);
    let year = raw.slice(4, 8);

    if (month.length === 2) {
      const m = parseInt(month, 10);
      month = Math.min(Math.max(m, 1), 12).toString().padStart(2, "0");
    }

    year = year.slice(0, 4);

    if (day.length === 2) {
      const d = parseInt(day, 10);
      let maxDay = 31;
      if (month.length === 2) {
        const y = year.length === 4 ? year : "2000";
        maxDay = moment(`${y}-${month}`, "YYYY-MM").daysInMonth();
      }

      day = Math.min(Math.max(d, 1), maxDay).toString().padStart(2, "0");
    }

    let formatted = day;
    if (month) formatted += `/${month}`;
    if (year) formatted += `/${year}`;
    input.value = formatted;
  };

  const handleCalendarClick = () => {
    setClickCount((prevCount) => prevCount + 1);
  };

  useEffect(() => {
    if (clickCount >= 3) {
      setShowHint(true);
    }
  }, [clickCount]);

  useEffect(() => {
    const datePickerButtons = document.querySelectorAll(".ant-picker-header");

    if (isCalendarOpen) {
      datePickerButtons.forEach((button) => {
        button.addEventListener("click", handleCalendarClick);
      });
    } else {
      datePickerButtons.forEach((button) => {
        button.removeEventListener("click", handleCalendarClick);
      });
    }

    return () => {
      datePickerButtons.forEach((button) => {
        button.removeEventListener("click", handleCalendarClick);
      });
    };
  }, [isCalendarOpen]);

  const handleOpenChange = (open) => {
    setIsCalendarOpen(open);
    if (!open) {
      setClickCount(0);
      setShowHint(false);
    }
  };

  useEffect(() => {
    const input = inputRef.current?.querySelector("input");
    if (input) {
      input.addEventListener("keyup", formatDate);
    }

    return () => {
      if (input) {
        input.removeEventListener("keyup", formatDate);
      }
    };
  }, []);

  const stableValue = useStableDatePickerValue(rest.value);

  return (
    <div
      className={classnames(
        "datepicker__lmf",
        {
          __error: touched && errors,
        },
        direction,
      )}
    >
      <Label
        label={label}
        icon={icon}
        required={required}
        optional={optional}
        right={right}
      />
      <div ref={inputRef} className={`datepicker__lmf--field`}>
        <DatePicker
          locale={locale}
          {...props}
          className={classnames({ "disabled--input": rest.disabled })}
          {...rest}
          value={stableValue}
          onOpenChange={handleOpenChange}
          renderExtraFooter={() =>
            showHint && (
              <p className="datePicker-hint-text">
                <MdOutlineKeyboard size={24} />
                <p>{DATE_PICKER.HINT}</p>
              </p>
            )
          }
        />
        {touched && errors && <p className={"input--errors"}>{errors}</p>}
      </div>
    </div>
  );
};

export default Simple;
